import { useEffect, useRef, useState, type CSSProperties } from 'react';

/**
 * iOS-parity RPM dial.
 *
 * Mirrors `Mobile/iOS/AquaStar/Views/Controls/DialView.swift` design:
 *   - 270° arc (gap at bottom).
 *   - 7 major ticks with numeric labels (0, 567, 1133, 1700, 2267, 2833, 3400).
 *   - 24 minor ticks between majors.
 *   - The minor tick nearest `minMotorSpeed` (~800 RPM) painted green —
 *     visual marker for the motor's lowest legal operating RPM.
 *   - Tapered needle that animates toward `currentRpm` (frame-tweened).
 *   - Center hub circle with brand outline.
 *   - Optional gradient power button at the bottom of the arc; renders
 *     when `onPowerToggle` is provided. Gradient = red (offline), grey
 *     (off), brand-blue (on).
 *   - Watts label below the RPM readout, above the power button.
 *   - Optional priming sub-label below the power button (pulsing
 *     drop icon) — kept absolute-positioned so the power button's
 *     geometry is identical with or without it.
 */

const PRIMING_COLOR = 'var(--aqua-priming)';
const ONLINE_COLOR = 'var(--aqua-online)';
const OFFLINE_COLOR = 'var(--aqua-offline)';
const PRIMARY = 'var(--aqua-primary)';
// Note: text colors inside the dial use var(--aqua-text-strong)
// directly so the readout flips with the theme.

export interface DialViewProps {
  currentRpm: number | null;
  isOn: boolean;
  isConnected: boolean;
  /** Watts being drawn — rendered inside the dial. */
  currentWatt?: number | null;
  /** Show the pulsing "Priming" sub-label below the power button. */
  isPriming?: boolean;
  /** When provided, renders a tappable power button at bottom of arc. */
  onPowerToggle?: () => void;
  /** Disables the power button (e.g. while a request is in flight). */
  powerBusy?: boolean;
  /**
   * When provided, renders two curved-arrow buttons flanking the
   * power button — left = +delta RPM, right = -delta RPM. Mirrors the
   * iOS `drawChangeSpeedButton` flanking buttons on `DialView.swift`.
   * The callback receives the delta the user requested (+100 or -100);
   * the parent computes the new RPM (clamping + dead-zone snap) and
   * dispatches the command.
   */
  onSpeedStep?: (delta: number) => void | Promise<void>;
  /** Disables the speed-step buttons while a request is in flight. */
  speedStepBusy?: boolean;
  maxRpm?: number;
  minMotorSpeed?: number;
  size?: number;
}

export function DialView({
  currentRpm,
  isOn,
  isConnected,
  currentWatt = null,
  isPriming = false,
  onPowerToggle,
  powerBusy = false,
  onSpeedStep,
  speedStepBusy = false,
  maxRpm = 3400,
  minMotorSpeed = 800,
  size = 320,
}: DialViewProps) {
  // Dial geometry — centered in a square SVG. 270° arc with the gap
  // at the bottom (6 o'clock). 0 RPM sits at bottom-LEFT, max RPM at
  // bottom-RIGHT — same orientation as the iOS DialView (its
  // `.rotationEffect(.degrees(-135))` puts tick 0 at bottom-left and
  // the last tick at bottom-right).
  //
  // In our angle convention, 0° = top, clockwise positive:
  //   225° = bottom-left  (0 RPM, start)
  //   270° = left
  //     0° = top          (mid RPM, ~1700)
  //    90° = right
  //   135° = bottom-right (max RPM, end)
  // sweep = 225° → 495° (= 135° mod 360) = 270° clockwise.
  const cx = size / 2;
  const cy = size / 2;
  const stroke = 14;
  const r = (size - stroke) / 2 - 30; // leave room for tick labels outside
  const startAngle = 225;
  const sweep = 270;

  // Tick layout
  const majorTickCount = 7;
  const minorPerSegment = 4; // 4 minors between every pair of majors
  const minorTickCount = (majorTickCount - 1) * minorPerSegment;
  const majorLen = 12;
  const minorLen = 6;

  const majorTickValues = Array.from({ length: majorTickCount }, (_, i) =>
    Math.round((i * maxRpm) / (majorTickCount - 1)),
  );

  // Animated needle — tween displayedRpm toward currentRpm so the
  // needle sweeps instead of snapping (matches iOS feel).
  const safeTarget =
    isOn && typeof currentRpm === 'number' && Number.isFinite(currentRpm)
      ? Math.min(Math.max(currentRpm, 0), maxRpm)
      : 0;
  const [displayedRpm, setDisplayedRpm] = useState(safeTarget);
  const rafRef = useRef<number | null>(null);
  const targetRef = useRef(safeTarget);
  targetRef.current = safeTarget;
  useEffect(() => {
    function tick() {
      setDisplayedRpm((prev) => {
        const target = targetRef.current;
        const diff = target - prev;
        if (Math.abs(diff) < 1) return target;
        // Step ~6% of the gap per frame (~250ms full sweep at 60fps).
        const step = diff * 0.06;
        return prev + (Math.abs(step) < 1 ? Math.sign(diff) : step);
      });
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  function polar(angleDeg: number, radius: number) {
    const a = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) };
  }
  function arcPath(fromAngle: number, toAngle: number, radius: number) {
    const start = polar(toAngle, radius);
    const end = polar(fromAngle, radius);
    const large = toAngle - fromAngle <= 180 ? 0 : 1;
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${large} 0 ${end.x} ${end.y}`;
  }

  const fillFraction = sweep > 0 ? Math.min(1, displayedRpm / maxRpm) : 0;
  const fillEndAngle = startAngle + sweep * fillFraction;
  const needleAngle = startAngle + sweep * fillFraction;
  const fillColor = !isConnected
    ? OFFLINE_COLOR
    : !isOn
      ? '#9aa0a6'
      : isPriming
        ? PRIMING_COLOR
        : PRIMARY;

  // Position of the power button at the bottom of the dial — between
  // the bottom of the arc and the bottom of the SVG box. Sized at
  // 22% of the dial diameter, matching the iOS 0.15-of-dial figure
  // which sits inside a tighter dial; web breathes a bit more.
  const powerButtonSize = Math.round(size * 0.22);
  const powerButtonY = cy + r * 0.6; // ~60% down from center toward bottom

  // Power-button gradient
  const powerGradientId = 'power-button-gradient';
  const powerOuter = !isConnected
    ? ['#e84d4d', '#c14545']
    : isOn
      ? [ONLINE_COLOR, '#239456']
      : ['#a8acb3', '#7a7d83'];

  // Find the minor-tick index closest to minMotorSpeed (for the green highlight)
  const minorTickValues = Array.from(
    { length: minorTickCount },
    (_, i) => ((i + 1) * maxRpm) / (minorTickCount + (majorTickCount - 1) - 1),
  );
  void minorTickValues;
  const fractionForMinMotor = minMotorSpeed / maxRpm;
  // Skip the minor that lines up exactly with a major (rare) — find the
  // nearest pure-minor index.
  function angleAt(fraction: number): number {
    return startAngle + sweep * fraction;
  }

  return (
    <div
      style={{
        width: size,
        height: size + 20,
        position: 'relative',
        margin: '0 auto',
        userSelect: 'none',
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label="Pump RPM dial"
      >
        <defs>
          <linearGradient id={powerGradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={powerOuter[0]} />
            <stop offset="100%" stopColor={powerOuter[1]} />
          </linearGradient>
        </defs>

        {/* Layered track — six tinted arc segments emulate the iOS
            AngularGradient (blue at low RPM, fading to red past the
            danger threshold ~2833 RPM). Wider than the progress
            stroke so it reads as a "background heat band" behind the
            active fill.

            Stop colors come from theme.css CSS variables so the band
            stays visible in BOTH light (5–18% on white) and dark
            (8–32% on near-black) — see --aqua-dial-heat-{1..6} in
            theme.css. */}
        {(() => {
          const segments = 6;
          const segColors = [
            'var(--aqua-dial-heat-1)',
            'var(--aqua-dial-heat-2)',
            'var(--aqua-dial-heat-3)',
            'var(--aqua-dial-heat-4)',
            'var(--aqua-dial-heat-5)',
            'var(--aqua-dial-heat-6)',
          ];
          const trackStroke = stroke + 14; // wider than the active arc
          const segLen = sweep / segments;
          return Array.from({ length: segments }, (_, i) => {
            const segStart = startAngle + i * segLen;
            // Tiny overlap on each segment join so anti-aliased edges
            // don't leave a hairline gap between adjacent colors.
            const segEnd = segStart + segLen + 0.5;
            return (
              <path
                key={`track-${i}`}
                d={arcPath(segStart, segEnd, r)}
                stroke={segColors[i]}
                strokeWidth={trackStroke}
                fill="none"
                strokeLinecap="butt"
              />
            );
          });
        })()}

        {/* Thin neutral guide along the inside of the colored band so
            tick marks read clearly without floating in tinted air. */}
        <path
          d={arcPath(startAngle, startAngle + sweep, r)}
          stroke="var(--aqua-dial-track)"
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
        />

        {/* Filled progress arc — drawn on top of the tinted track. */}
        {fillFraction > 0 && (
          <path
            d={arcPath(startAngle, fillEndAngle, r)}
            stroke={fillColor}
            strokeWidth={stroke}
            fill="none"
            strokeLinecap="round"
          />
        )}

        {/* Major ticks + numeric labels */}
        {majorTickValues.map((value, i) => {
          const fraction = i / (majorTickCount - 1);
          const angle = angleAt(fraction);
          const inner = polar(angle, r - stroke / 2 - 4);
          const outer = polar(angle, r - stroke / 2 - 4 - majorLen);
          const labelPt = polar(angle, r - stroke / 2 - 4 - majorLen - 14);
          return (
            <g key={`maj-${i}`}>
              <line
                x1={inner.x}
                y1={inner.y}
                x2={outer.x}
                y2={outer.y}
                stroke={PRIMARY}
                strokeWidth={2.5}
                strokeLinecap="round"
              />
              <text
                x={labelPt.x}
                y={labelPt.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="var(--aqua-text-muted)"
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* Minor ticks (skip positions that coincide with majors) */}
        {Array.from({ length: minorTickCount }, (_, i) => {
          // Place minors evenly between majors. Each segment between
          // majors is split into (minorPerSegment + 1) sub-segments;
          // the (minorPerSegment) interior split-points are minors.
          const segIndex = Math.floor(i / minorPerSegment); // 0..(majorTickCount-2)
          const within = (i % minorPerSegment) + 1; // 1..minorPerSegment
          const fraction =
            (segIndex + within / (minorPerSegment + 1)) / (majorTickCount - 1);
          const angle = angleAt(fraction);
          const inner = polar(angle, r - stroke / 2 - 4);
          const outer = polar(angle, r - stroke / 2 - 4 - minorLen);

          // Highlight the minor closest to minMotorSpeed in green.
          const isMinMotor =
            Math.abs(fraction - fractionForMinMotor) <
            0.5 / (minorTickCount + 1);

          return (
            <line
              key={`min-${i}`}
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
              stroke={isMinMotor ? ONLINE_COLOR : 'var(--aqua-dial-tick-minor)'}
              strokeWidth={isMinMotor ? 2.5 : 1.25}
              strokeLinecap="round"
            />
          );
        })}

        {/* Needle — tapered triangle pointing outward from center */}
        {(() => {
          const tipR = r - stroke / 2 - 6;
          const baseR = 10;
          const baseHalfWidthDeg = 4;
          const tipPt = polar(needleAngle, tipR);
          const baseLeft = polar(needleAngle - baseHalfWidthDeg, baseR);
          const baseRight = polar(needleAngle + baseHalfWidthDeg, baseR);
          const needleColor = !isConnected
            ? OFFLINE_COLOR
            : isPriming
              ? PRIMING_COLOR
              : isOn
                ? PRIMARY
                : '#aab0ba';
          return (
            <polygon
              points={`${tipPt.x},${tipPt.y} ${baseRight.x},${baseRight.y} ${baseLeft.x},${baseLeft.y}`}
              fill={needleColor}
              opacity={isConnected ? 1 : 0.55}
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.18))' }}
            />
          );
        })()}

        {/* Center hub */}
        <circle
          cx={cx}
          cy={cy}
          r={9}
          fill="var(--aqua-surface)"
          stroke={PRIMARY}
          strokeWidth={3}
        />

        {/* RPM readout — always show the number (0 when off, "—" only
            when offline). Number sized at 9% of dial width (was 10%);
            "RPM" caption pushed down to size*0.075 below the number's
            center so the two don't crowd each other. Group origin is
            cy - size*0.13 (was -0.10) to keep both texts in the upper
            half of the dial above the live watt readout. */}
        <g transform={`translate(${cx} ${cy - size * 0.13})`}>
          <text
            textAnchor="middle"
            dominantBaseline="middle"
            fill={isConnected ? 'var(--aqua-text-strong)' : 'var(--aqua-text-muted)'}
            style={{
              fontSize: size * 0.09,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: -0.5,
            }}
          >
            {!isConnected ? '—' : Math.round(displayedRpm).toLocaleString()}
          </text>
          <text
            y={size * 0.075}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--aqua-text-muted)"
            style={{
              fontSize: size * 0.038,
              fontWeight: 500,
              letterSpacing: 1.4,
            }}
          >
            RPM
          </text>
        </g>

        {/* Watts inside dial — small, between center and power button */}
        {isOn && isConnected && typeof currentWatt === 'number' && currentWatt > 0 && (
          <g transform={`translate(${cx} ${cy + size * 0.07})`}>
            <text
              textAnchor="middle"
              dominantBaseline="middle"
              fill="var(--aqua-text-strong)"
              style={{
                fontSize: size * 0.06,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {currentWatt.toLocaleString()}
              <tspan
                fill="var(--aqua-text-muted)"
                style={{ fontSize: size * 0.04, fontWeight: 500 }}
              >
                {' '}
                W
              </tspan>
            </text>
          </g>
        )}
      </svg>

      {/* Power button (HTML overlay so it can be a real <button>) */}
      {onPowerToggle && (
        <button
          type="button"
          onClick={onPowerToggle}
          disabled={!isConnected || powerBusy}
          aria-label={isOn ? 'Turn pump off' : 'Turn pump on'}
          style={{
            position: 'absolute',
            left: cx - powerButtonSize / 2,
            top: powerButtonY - powerButtonSize / 2,
            width: powerButtonSize,
            height: powerButtonSize,
            borderRadius: '50%',
            border: 'none',
            cursor: isConnected && !powerBusy ? 'pointer' : 'not-allowed',
            background: !isConnected
              ? `linear-gradient(135deg, ${OFFLINE_COLOR}, ${OFFLINE_COLOR}66)`
              : isOn
                ? `linear-gradient(135deg, ${ONLINE_COLOR}, #239456)`
                : `linear-gradient(135deg, #a8acb3, #7a7d83)`,
            boxShadow: '0 6px 14px rgba(16, 28, 66, 0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            transition: 'transform 0.1s ease',
            opacity: powerBusy ? 0.7 : 1,
          }}
          onMouseDown={(e) => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.95)';
          }}
          onMouseUp={(e) => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
          }}
        >
          <PowerGlyph size={powerButtonSize * 0.5} />
        </button>
      )}

      {/* Curved-arrow speed-step buttons — flank the power button on
          both sides, matching iOS `drawChangeSpeedButton` on
          Mobile/iOS/AquaStar/Views/Controls/DialView.swift.
            - LEFT  = +100 RPM (mirrored arrow, loop curves right)
            - RIGHT = -100 RPM (un-mirrored arrow, loop curves left)
          Both arrow tips point UP toward the gauge center.
          Rotated ±15° so the button body subtly follows the arc curve.
          Gradient: red when offline, green when ON, gray otherwise —
          same hex-literal second-stop pattern as the power button so
          `var(--…)` string-concatenation never produces invalid CSS. */}
      {onSpeedStep && (() => {
        const stepBtnSize = Math.round(size * 0.14);
        // Sit slightly below the power-button center, roughly where
        // the arc terminates at bottom-left / bottom-right (the
        // colored band's bottom-corner endpoints).
        const stepBtnY = cy + r * 0.65 - stepBtnSize / 2;
        // Negative offset: push buttons OUTSIDE the dial container
        // so they clear the heat-band's outer edge entirely. Parent
        // card-body has default `overflow: visible`, so this is safe.
        const stepBtnEdgeOffset = -Math.round(size * 0.08);
        const stepDisabled = !isConnected || speedStepBusy;
        const stepGradient = !isConnected
          ? `linear-gradient(135deg, ${OFFLINE_COLOR}, #c14545)`
          : isOn
            ? `linear-gradient(135deg, ${ONLINE_COLOR}, #239456)`
            : `linear-gradient(135deg, #a8acb3, #7a7d83)`;
        const baseStyle: CSSProperties = {
          position: 'absolute',
          top: stepBtnY,
          width: stepBtnSize,
          height: stepBtnSize,
          borderRadius: '50%',
          border: 'none',
          cursor: stepDisabled ? 'not-allowed' : 'pointer',
          background: stepGradient,
          boxShadow: '0 4px 10px rgba(16, 28, 66, 0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: stepBtnSize * 0.62,
          lineHeight: 1,
          opacity: stepDisabled ? 0.55 : 1,
          transition: 'transform 0.1s ease',
          padding: 0,
        };
        return (
          <>
            {/* LEFT — increment (+100). Arrow mirrored on the X axis +
                rotated 90° so the curl points upward from below. The
                outer rotation tilts the whole button +15° to match
                the arc. */}
            <button
              type="button"
              onClick={() => { void onSpeedStep(100); }}
              disabled={stepDisabled}
              aria-label="Increase pump speed by 100 RPM"
              style={{
                ...baseStyle,
                left: stepBtnEdgeOffset,
                transform: 'rotate(15deg)',
              }}
              onMouseDown={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform =
                  'rotate(15deg) scale(0.93)';
              }}
              onMouseUp={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform =
                  'rotate(15deg) scale(1)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform =
                  'rotate(15deg) scale(1)';
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  // CSS transforms compose right-to-left, so this
                  // applies rotate(90deg) FIRST (arrow tip → up), then
                  // scaleX(-1) (mirror horizontally → loop curves
                  // right). The result is the iOS-style mirror of the
                  // right button: tip up, loop on the inside.
                  transform: 'scaleX(-1) rotate(90deg)',
                  fontWeight: 600,
                }}
              >
                ⤺
              </span>
            </button>
            {/* RIGHT — decrement (-100). Un-mirrored, rotated 90°. */}
            <button
              type="button"
              onClick={() => { void onSpeedStep(-100); }}
              disabled={stepDisabled}
              aria-label="Decrease pump speed by 100 RPM"
              style={{
                ...baseStyle,
                right: stepBtnEdgeOffset,
                transform: 'rotate(-15deg)',
              }}
              onMouseDown={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform =
                  'rotate(-15deg) scale(0.93)';
              }}
              onMouseUp={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform =
                  'rotate(-15deg) scale(1)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform =
                  'rotate(-15deg) scale(1)';
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  transform: 'rotate(90deg)',
                  fontWeight: 600,
                }}
              >
                ⤺
              </span>
            </button>
          </>
        );
      })()}

      {/* Priming sub-label — absolute-positioned so the power button
          geometry is identical whether it shows or not (matches the
          iOS DialView design rule). */}
      {isPriming && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: powerButtonY + powerButtonSize / 2 + 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            color: PRIMING_COLOR,
            fontSize: 13,
            fontWeight: 600,
            pointerEvents: 'none',
            // Sit above the curved-arrow speed buttons (which use
            // implicit z-index from DOM order). The priming sub-label
            // is informational and must never be obscured by the
            // step buttons even if their box overlaps this y-band.
            zIndex: 2,
          }}
        >
          <DropPulse color={PRIMING_COLOR} />
          <span>Priming</span>
        </div>
      )}
    </div>
  );
}

/** Apple-style power glyph — circle with vertical bar through the top. */
function PowerGlyph({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 3v9"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M7.5 6.5a7 7 0 1 0 9 0"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** Small pulsing water-drop used by the priming sub-label. */
function DropPulse({ color }: { color: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        animation: 'aquastar-drop-pulse 0.9s ease-in-out infinite alternate',
      }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 16 16" width="14" height="14">
        <path
          d="M8 1.5 C 5.5 5 3.5 7 3.5 9.5 a4.5 4.5 0 1 0 9 0 C 12.5 7 10.5 5 8 1.5 Z"
          fill={color}
        />
      </svg>
      <style>{`
        @keyframes aquastar-drop-pulse {
          from { transform: scale(0.9); opacity: 0.55; }
          to   { transform: scale(1.15); opacity: 1; }
        }
      `}</style>
    </span>
  );
}
